import React, { useState, useEffect } from 'react';
import { Table, Dropdown, Menu, Button, Space, Typography, Modal, message, Descriptions, Input } from 'antd';
import { EyeOutlined, CloseOutlined, DownOutlined, DeleteOutlined, MailOutlined } from '@ant-design/icons';
import './AdminSupportProvider.css';

const { Title } = Typography;
const { TextArea } = Input;

const AdminSupportProvider = () => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isReplyModalVisible, setIsReplyModalVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [ticketToReply, setTicketToReply] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');

  // Fetch tickets from backend
  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await fetch('/backend/superadmin_get_tickets.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Erreur de parsing JSON:', e, 'Texte reçu:', text);
        throw new Error('Réponse non-JSON');
      }
      if (response.ok) {
        console.log('Données tickets reçues:', data.tickets);
        setDataSource(data.tickets || []);
      } else {
        if (response.status === 403) {
          message.error('Session expirée. Veuillez vous reconnecter.');
        } else {
          message.error(data.error || `Erreur HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Erreur de réseau:', error.message);
      message.error('Erreur réseau lors de la récupération des tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // Handle view
  const handleView = (record) => {
    console.log('View:', record);
    setSelectedTicket(record);
    setIsViewModalVisible(true);
  };

  // Handle close
  const handleClose = async (record) => {
    console.log('Close:', record);
    try {
      const response = await fetch('/backend/superadmin_close_ticket.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: record.id }),
      });
      const data = await response.json();
      console.log('Réponse close:', data);
      if (response.ok) {
        message.success(data.message || 'Ticket fermé avec succès');
        fetchTickets();
      } else {
        message.error(data.error || `Erreur HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur réseau lors de la fermeture:', error);
      message.error('Erreur réseau lors de la fermeture');
    }
  };

  // Handle reply
  const handleReply = (record) => {
    console.log('Reply:', record);
    setTicketToReply(record);
    setReplyMessage('');
    setIsReplyModalVisible(true);
  };

  // Handle reply confirmation
  const handleReplyConfirm = async () => {
    if (!ticketToReply || !replyMessage.trim()) {
      message.error('Veuillez entrer un message de réponse');
      return;
    }
    console.log('Confirmation de réponse pour ID:', ticketToReply.id, 'Message:', replyMessage);
    try {
      const response = await fetch('/backend/superadmin_reply_ticket.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: ticketToReply.id, message: replyMessage }),
      });
      const data = await response.json();
      console.log('Réponse reply:', data);
      if (response.ok) {
        message.success(data.message || 'Réponse envoyée avec succès');
        setIsReplyModalVisible(false);
        setTicketToReply(null);
        setReplyMessage('');
        fetchTickets();
      } else {
        message.error(data.error || `Erreur HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur réseau lors de l\'envoi de la réponse:', error);
      message.error('Erreur réseau lors de l\'envoi de la réponse');
    }
  };

  // Handle reply cancel
  const handleReplyCancel = () => {
    console.log('Réponse annulée');
    setIsReplyModalVisible(false);
    setTicketToReply(null);
    setReplyMessage('');
  };

  // Handle delete
  const handleDelete = (record) => {
    console.log('handleDelete appelé pour:', record);
    setTicketToDelete(record);
    setIsDeleteModalVisible(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!ticketToDelete) return;
    console.log('Confirmation de suppression pour ID:', ticketToDelete.id);
    try {
      const response = await fetch('/backend/superadmin_delete_ticket.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: ticketToDelete.id }),
      });
      console.log('Réponse de suppression:', response.status, response.statusText);
      const data = await response.json();
      console.log('Données de réponse:', data);
      if (response.ok) {
        message.success(data.message || 'Ticket supprimé avec succès');
        setIsDeleteModalVisible(false);
        setTicketToDelete(null);
        fetchTickets();
      } else {
        message.error(data.error || `Erreur HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur réseau lors de la suppression:', error);
      message.error('Erreur réseau lors de la suppression');
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    console.log('Suppression annulée');
    setIsDeleteModalVisible(false);
    setTicketToDelete(null);
  };

  // Handle view modal close
  const handleViewModalClose = () => {
    setIsViewModalVisible(false);
    setSelectedTicket(null);
  };

  // Menu for dropdown actions
  const menu = (record) => (
    <Menu>
      <Menu.Item key="1" icon={<EyeOutlined />} onClick={() => handleView(record)}>
        View
      </Menu.Item>
      <Menu.Item key="2" icon={<CloseOutlined />} onClick={() => handleClose(record)}>
        Close
      </Menu.Item>
      <Menu.Item key="3" icon={<MailOutlined />} onClick={() => handleReply(record)}>
        Reply
      </Menu.Item>
      <Menu.Item key="4" icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
        Delete
      </Menu.Item>
    </Menu>
  );

  // Define columns
  const columns = [
    { title: 'Email', dataIndex: 'admin_email', key: 'admin_email' },
    { title: 'Issue', dataIndex: 'issue', key: 'issue', render: (issue) => (
      <span className="issue-text">{issue}</span>
    )},
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status', 
      render: (status) => (
        <span className={
          status === 'Resolved' ? 'status-resolved' :
          status === 'Closed' ? 'status-closed' :
          status === 'In Progress' ? 'status-in-progress' : 'status-pending'
        }>
          {status}
        </span>
      ),
    },
    { title: 'Date', dataIndex: 'created_at', key: 'created_at' },
    { 
      title: 'Action', 
      key: 'action', 
      render: (_, record) => (
        <Space size="middle">
          <Dropdown overlay={menu(record)} trigger={['click']}>
            <Button icon={<DownOutlined />} size="small">
              Actions
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-support-provider-container">
      <Title level={3} className="admin-support-provider-title">Admin Support Tickets</Title>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={{ pageSize: 5 }}
        className="admin-support-provider-table"
        loading={loading}
      />
      {/* View Modal */}
      <Modal
        title={`Détails du ticket - ${selectedTicket?.admin_email}`}
        open={isViewModalVisible}
        onOk={handleViewModalClose}
        onCancel={handleViewModalClose}
        okText="Fermer"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={600}
      >
        {selectedTicket && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">{selectedTicket.id}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedTicket.admin_email}</Descriptions.Item>
            <Descriptions.Item label="Problème">{selectedTicket.issue}</Descriptions.Item>
            <Descriptions.Item label="Requête">{selectedTicket.request}</Descriptions.Item>
            <Descriptions.Item label="Fichier joint">
              {selectedTicket.file_path ? (
                <a href={`/backend/${selectedTicket.file_path}`} target="_blank" rel="noopener noreferrer">
                  Voir
                </a>
              ) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Statut">{selectedTicket.status}</Descriptions.Item>
            <Descriptions.Item label="Créé le">{selectedTicket.created_at}</Descriptions.Item>
            <Descriptions.Item label="Mis à jour le">{selectedTicket.updated_at || 'N/A'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
      {/* Reply Modal */}
      <Modal
        title={`Répondre au ticket - ${ticketToReply?.admin_email}`}
        open={isReplyModalVisible}
        onOk={handleReplyConfirm}
        onCancel={handleReplyCancel}
        okText="Envoyer"
        cancelText="Annuler"
      >
        <p>Entrez votre réponse ci-dessous :</p>
        <TextArea
          rows={4}
          value={replyMessage}
          onChange={(e) => setReplyMessage(e.target.value)}
          placeholder="Votre message..."
        />
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirmer la suppression"
        open={isDeleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Supprimer"
        okType="danger"
        cancelText="Annuler"
      >
        <p>
          Voulez-vous vraiment supprimer le ticket pour {ticketToDelete?.admin_email} ({ticketToDelete?.issue}) ?
        </p>
      </Modal>
    </div>
  );
};

export default AdminSupportProvider;